import { create } from "zustand";
import { toast } from "react-toastify";
import {
  getCurrentDateTime,
  getStartOfCurrentDate,
} from "../../../Common/helper/helpar";
import {
  getAllRfMasterReport,
  getAssetScanTrackingReportByTagId,
} from "../../../Apis/rfidApi";

const getDefaultReportDates = () => {
  const from = new Date();
  from.setDate(from.getDate() - 7);
  const year = from.getFullYear();
  const month = String(from.getMonth() + 1).padStart(2, "0");
  const day = String(from.getDate()).padStart(2, "0");
  return {
    fromDate: `${year}-${month}-${day}T00:00`,
    toDate: getCurrentDateTime() || getStartOfCurrentDate(),
  };
};

const getErrorMessage = (data, fallback) => {
  if (typeof data === "string" && data) return data;
  if (data?.message) return data.message;
  return fallback;
};

export const getReportRowKey = (row, fallbackIndex = 0) => {
  if (row?.rowNumber != null && row?.rowNumber !== "") {
    return `report-${row.rowNumber}`;
  }

  const imei = row?.imeiNo ?? row?.imeiSlMfgNo ?? row?.imeiOrSerialOrMfgNo;
  const assetCode = row?.assetCode ?? row?.rfidCode;
  const assetId = row?.assetId ?? row?.assetCodeId;

  const parts = [imei, assetCode, assetId].filter(
    (value) => value != null && value !== "" && value !== "N/A"
  );

  if (parts.length > 0) {
    return `report-${parts.join("-")}`;
  }

  return `report-row-${fallbackIndex}`;
};

const extractReportList = (response) => {
  if (!response) return [];
  if (Array.isArray(response)) return response;
  if (Array.isArray(response?.data)) return response.data;
  if (Array.isArray(response?.Data)) return response.Data;
  if (Array.isArray(response?.items)) return response.items;
  if (Array.isArray(response?.rfMasterReports)) return response.rfMasterReports;
  if (Array.isArray(response?.rfidMasterReports)) return response.rfidMasterReports;

  const nested = response?.data ?? response?.Data ?? response?.result;
  if (nested && typeof nested === "object" && !Array.isArray(nested)) {
    if (Array.isArray(nested.data)) return nested.data;
    if (Array.isArray(nested.Data)) return nested.Data;
  }

  return [];
};

let reportFetchSeq = 0;

const resolveReportPagination = (response, requestPagination, rowCount = 0) => {
  const pageSize =
    Number(response?.pageSize) || requestPagination.pageSize || 10;
  const pageNumber =
    Number(response?.pageNumber) || requestPagination.pageNumber || 1;
  const totalRecords = Number(response?.totalRecords) || rowCount || 0;

  let totalPages =
    Number(response?.totalPages) ||
    (totalRecords > 0 ? Math.max(1, Math.ceil(totalRecords / pageSize)) : 1);

  if (rowCount > 0 && totalRecords > rowCount && totalPages === 1) {
    totalPages = Math.ceil(totalRecords / rowCount);
  }

  return {
    pageNumber,
    pageSize,
    totalRecords,
    totalPages,
  };
};

const normalizeCoordinate = (value) =>
  value != null ? String(value).replace(",", ".") : "";

const normalizeDisplayValue = (value, fallback = "N/A") => {
  if (value == null) return fallback;
  const text = String(value).trim();
  return text === "" ? fallback : text;
};

/** Normalize API datetimes like "2026-07-31 10:55:01" for reliable parsing */
const normalizeApiDateTime = (value) => {
  if (value == null) return "";
  const raw = String(value).trim();
  if (!raw) return "";
  if (/^\d{4}-\d{2}-\d{2} \d{2}:\d{2}/.test(raw)) {
    return raw.replace(" ", "T");
  }
  return raw;
};

const mapReportData = (list = []) =>
  list.map((item) => {
    const imeiSlMfgNo = normalizeDisplayValue(
      item.imeiOrSerialOrMfgNo ??
        item.imeiNo ??
        item.ImeiNo ??
        item.imeiSlMfgNo
    );

    return {
      ...item,
      rfidMasterId:
        item.rfidMasterId ??
        item.rowNumber ??
        item.assetId ??
        item.assetCodeId ??
        item.rfMasterId,
      assetRfidId:
        item.assetRfidId ??
        item.rowNumber ??
        item.assetId ??
        item.assetCodeId ??
        item.rfidMasterId ??
        item.rfMasterId,
      rfidCode: normalizeDisplayValue(
        item.assetCode ?? item.rfidCode ?? item.assetRfidCode,
        "-"
      ),
      rfidCategoryName: normalizeDisplayValue(
        item.assetCategory ??
          item.rfidCategoryName ??
          item.assetCategoryName ??
          item.rfidCategory ??
          item.assetName
      ),
      rfidDeviceName: normalizeDisplayValue(
        item.rfDeviceName ?? item.rfidDeviceName ?? item.assetName
      ),
      rfidTag: item.rfidTag ?? item.otherNo ?? item.TId ?? "N/A",
      imeiNo: imeiSlMfgNo === "N/A" ? "" : imeiSlMfgNo,
      imeiSlMfgNo,
      vehicleNo: normalizeDisplayValue(
        item.tagVehicleNo ??
          item.TagVehicleNo ??
          item.vehicleNo ??
          item.vehicleNumber
      ),
      zoneName: normalizeDisplayValue(
        item.zone ?? item.zoneName ?? item.zoneNo ?? item.ZoneNo
      ),
      wardNo: normalizeDisplayValue(
        item.wardName ?? item.wardNo ?? item.circleName ?? item.WardNo
      ),
      scannedByReader: normalizeDisplayValue(
        item.scannedByReader ?? item.ScannedByReader
      ),
      readerCategory: normalizeDisplayValue(
        item.readerCategory ?? item.ReaderCategory
      ),
      readerVehicle: normalizeDisplayValue(
        item.readerVehicleNo ?? item.readerVehicle ?? item.ReaderVehicleNo
      ),
      readerZone: normalizeDisplayValue(
        item.readerZone ?? item.readerZoneNo ?? item.ReaderZoneNo
      ),
      assetTypeName: normalizeDisplayValue(
        item.assetType ??
          item.assetTypeName ??
          item.rfidTypeName ??
          item.AssetTypeName ??
          item.RFIDType
      ),
      readerWard: normalizeDisplayValue(
        item.readerWard ??
          item.ReaderWard ??
          item.readerWardNo ??
          item.ReaderWardNo
      ),
      readerZoneId: item.readerZoneId ?? item.ReaderZoneId ?? "",
      readerWardId: item.readerWardId ?? item.ReaderWardId ?? "",
      totalScanned:
        item.totalScanned ??
        item.TotalScanned ??
        item.scannedCount ??
        item.TotalScan ??
        0,
      actualShift: normalizeDisplayValue(
        item.actualShift ??
          item.shiftName ??
          item.ShiftName ??
          item.shift
      ),
      shift: item.actualShift ?? item.shiftName ?? item.ShiftName ?? item.shift ?? "",
      latitude: normalizeCoordinate(item.latitude ?? item.lat ?? item.ULat),
      longitude: normalizeCoordinate(item.longitude ?? item.lng ?? item.ULng),
      lastScannedOn: normalizeApiDateTime(
        item.lastScannedTime ??
          item.lastScannedOn ??
          item.LastScannedOn ??
          item.firstScannedOn ??
          item.lastSyncOn
      ),
      inTime: normalizeApiDateTime(item.inTime ?? item.InTime),
      outTime: normalizeApiDateTime(item.outTime ?? item.OutTime),
      scanDetails: [],
      isActive: item.isActive ?? item.status ?? true,
    };
  });

export const mapScannedTagData = (list = []) =>
  list.map((item, index) => {
    const display = (value, fallback = "N/A") => {
      if (value == null) return fallback;
      const text = String(value).trim();
      return text === "" ? fallback : text;
    };

    const scannedOnRaw =
      item.ScannedOn ?? item.scannedOn ?? item.SyncOn ?? item.syncOn ?? "";
    const scannedOnNormalized =
      scannedOnRaw &&
      /^\d{4}-\d{2}-\d{2} \d{2}:\d{2}/.test(String(scannedOnRaw).trim())
        ? String(scannedOnRaw).trim().replace(" ", "T")
        : scannedOnRaw;

    let scannedOn = "N/A";
    if (scannedOnNormalized) {
      const date = new Date(scannedOnNormalized);
      scannedOn = Number.isNaN(date.getTime())
        ? String(scannedOnRaw)
        : date.toLocaleString("en-GB", {
            day: "2-digit",
            month: "short",
            year: "numeric",
            hour: "2-digit",
            minute: "2-digit",
            hour12: true,
          });
    }

    const statusLabel = display(
      item.Status ?? item.status ?? item.statusLabel,
      ""
    );

    return {
      tagId: display(
        item.TagId ?? item.TId ?? item.tId ?? item.tagId,
        `TAG${String(index + 1).padStart(5, "0")}`
      ),
      readerId: display(
        item.ReaderId ?? item.readerId ?? item.imeiNo
      ),
      scannedOn,
      actualShift: display(
        item.ActualShift ??
          item.actualShift ??
          item.ShiftName ??
          item.shiftName
      ),
      readerInstalledArea: display(
        item.ReaderInstalledArea ??
          item.readerInstalledArea ??
          item.AssetAreaType ??
          item.assetAreaType
      ),
      readerVehicle: display(
        item.ReaderVehicleNo ??
          item.readerVehicleNo ??
          item.readerVehicle ??
          item.OtherNo ??
          item.otherNo
      ),
      readerCategory: display(
        item.ReaderCategory ??
          item.readerCategory ??
          item.AssetName ??
          item.assetName
      ),
      readerZone: display(
        item.ReaderZone ??
          item.readerZone ??
          item.ZoneNo ??
          item.zoneNo
      ),
      readerWard: display(
        item.ReaderWard ??
          item.readerWard ??
          item.WardNo ??
          item.wardNo
      ),
      statusLabel: statusLabel || "N/A",
      status: statusLabel || item.status,
      isActive:
        typeof item.isActive === "boolean"
          ? item.isActive
          : statusLabel
          ? ["IN", "ACTIVE", "TRUE", "1"].includes(
              String(statusLabel).toUpperCase()
            )
          : true,
    };
  });

export const DEFAULT_REPORT_ZONE_ID = "1";
export const DEFAULT_REPORT_ZONE_NAME = "Delhi Central Zone";

export const useRfidReportsStore = create((set, get) => ({
  reportRows: [],
  rfidScannedTags: {},
  isLoading: false,
  isScannedTagsLoading: false,
  error: "",
  pagination: {
    pageNumber: 1,
    pageSize: 10,
    totalRecords: 0,
    totalPages: 1,
  },
  filters: {
    zoneId: DEFAULT_REPORT_ZONE_ID,
    circleId: "",
    assetTypeId: "",
    assetCategoryId: "",
    shift: "",
    ...getDefaultReportDates(),
  },

  updateFilter: (name, value) =>
    set((state) => ({
      filters: { ...state.filters, [name]: value },
    })),

  resetFilters: () => {
    const defaultDates = getDefaultReportDates();
    set({
      filters: {
        zoneId: DEFAULT_REPORT_ZONE_ID,
        circleId: "",
        assetTypeId: "",
        assetCategoryId: "",
        shift: "",
        ...defaultDates,
      },
      rfidScannedTags: {},
      pagination: { ...get().pagination, pageNumber: 1 },
    });
    return get().fetchRfidReports({
      pageNumber: 1,
      zoneId: DEFAULT_REPORT_ZONE_ID,
      ...defaultDates,
    });
  },

  setReportPage: (pageNumber) => {
    set((state) => ({
      pagination: { ...state.pagination, pageNumber },
    }));
    return get().fetchRfidReports({ pageNumber });
  },

  setReportPageSize: (pageSize) => {
    set((state) => ({
      pagination: { ...state.pagination, pageSize, pageNumber: 1 },
    }));
    return get().fetchRfidReports({ pageNumber: 1, pageSize });
  },

  fetchRfMasterScannedTags: async (row, rowIndex = 0) => {
    const imeiNo =
      row?.imeiNo ?? row?.imeiSlMfgNo ?? row?.imeiOrSerialOrMfgNo;
    const rowKey = getReportRowKey(row, rowIndex);
    if (!imeiNo || imeiNo === "N/A" || !rowKey) return [];

    set({ isScannedTagsLoading: true });
    try {
      const response = await getAssetScanTrackingReportByTagId({ imeiNo });
      const tagList = response?.data ?? extractReportList(response);
      const mappedTags = mapScannedTagData(tagList);

      set((state) => ({
        rfidScannedTags: {
          ...state.rfidScannedTags,
          [rowKey]: mappedTags,
        },
      }));

      return mappedTags;
    } catch (error) {
      const message = getErrorMessage(
        error?.response?.data,
        "Failed to load scanned tag details."
      );
      toast.error(message);
      set((state) => ({
        rfidScannedTags: { ...state.rfidScannedTags, [rowKey]: [] },
      }));
      return [];
    } finally {
      set({ isScannedTagsLoading: false });
    }
  },

  fetchRfidReports: async (queryParams = {}) => {
    const fetchId = ++reportFetchSeq;
    set({ isLoading: true, error: "" });
    try {
      const { filters, pagination } = get();
      const mergedFilters = { ...filters, ...queryParams };
      const mergedPagination = {
        pageNumber: queryParams.pageNumber ?? pagination.pageNumber,
        pageSize: queryParams.pageSize ?? pagination.pageSize,
      };

      const response = await getAllRfMasterReport({
        zoneId: mergedFilters.zoneId || undefined,
        circleId: mergedFilters.circleId || undefined,
        assetTypeId: mergedFilters.assetTypeId || undefined,
        assetCategoryId: mergedFilters.assetCategoryId || undefined,
        shift: mergedFilters.shift || undefined,
        fromDate: mergedFilters.fromDate || undefined,
        toDate: mergedFilters.toDate || undefined,
        pageNumber: mergedPagination.pageNumber,
        pageSize: mergedPagination.pageSize,
      });

      if (fetchId !== reportFetchSeq) {
        return { success: false, stale: true };
      }

      const reportList = response?.data ?? extractReportList(response);
      const mappedData = mapReportData(reportList);
      const resolvedPagination = resolveReportPagination(
        response,
        mergedPagination,
        mappedData.length
      );

      set({
        reportRows: mappedData,
        rfidScannedTags: {},
        filters: {
          zoneId: mergedFilters.zoneId ?? filters.zoneId,
          circleId: mergedFilters.circleId ?? filters.circleId,
          assetTypeId: mergedFilters.assetTypeId ?? filters.assetTypeId,
          assetCategoryId: mergedFilters.assetCategoryId ?? filters.assetCategoryId,
          shift: mergedFilters.shift ?? filters.shift,
          fromDate: mergedFilters.fromDate ?? filters.fromDate,
          toDate: mergedFilters.toDate ?? filters.toDate,
        },
        pagination: resolvedPagination,
      });

      return { success: true, data: mappedData };
    } catch (error) {
      if (fetchId !== reportFetchSeq) {
        return { success: false, stale: true };
      }

      const message = getErrorMessage(
        error?.response?.data,
        "Failed to load RFID reports."
      );
      set({
        reportRows: [],
        error: message,
        pagination: {
          pageNumber: 1,
          pageSize: 10,
          totalRecords: 0,
          totalPages: 1,
        },
      });
      toast.error(message);
      return { success: false, message };
    } finally {
      if (fetchId === reportFetchSeq) {
        set({ isLoading: false });
      }
    }
  },
}));
